import { useState, useEffect } from 'react'
import { createAdminApi, GesturePoolStats as Stats } from '../services/adminApi'
import styles from '../pages/CharactersPage.module.css'

interface Props {
  characterId: string
  // refreshToken re-runs the stats fetch whenever its value changes — the
  // parent bumps it after a pool mutation (delete a row / clear the pool) so the
  // headline counts reconcile with the entries list.
  refreshToken?: number
  // onTargetPoolSize lifts the server-supplied target to the parent, whose
  // entries table renders "n / target variants" on each per-key header row.
  // The target is never hardcoded in the UI (see gesture_pools_api.go's
  // SetGesturePoolTargetSize), and this panel is the only thing that fetches
  // stats. Pass a STABLE callback (a useState setter) — it is an effect dep.
  onTargetPoolSize?: (n: number) => void
}

// One stacked-bar segment. Zero-value segments are dropped from the bar but
// still listed (muted) in the legend, so a segment doesn't vanish the moment
// its count hits zero.
interface Seg {
  label: string
  value: number
  color: string
}

// shortDate renders an ISO timestamp as "Jul 14" for the pool-span line. Bad
// input passes through untouched rather than throwing.
function shortDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

// round1 trims the SQL-precise mean to one decimal for display.
function round1(n: number): string {
  return (Math.round(n * 10) / 10).toString()
}

// StackedBar renders an 8px proportional bar (zero segments omitted) followed
// by a mono legend that lists every segment, zeros dimmed.
function StackedBar({ label, segments }: { label: string; segments: Seg[] }) {
  const shown = segments.filter(s => s.value > 0)
  return (
    <div className={styles.statBarRow}>
      <span className={styles.statBarLabel}>{label}</span>
      <div className={styles.statBarWrap}>
        <div className={styles.statBar}>
          {shown.map(s => (
            <div
              key={s.label}
              className={styles.statBarSeg}
              style={{ flexGrow: s.value, background: s.color }}
              title={`${s.label} ${s.value}`}
            />
          ))}
        </div>
        <span className={styles.statLegend}>
          {segments.map((s, i) => (
            <span key={s.label} className={s.value === 0 ? styles.statVariantsBelow : undefined}>
              {i > 0 ? ' · ' : ''}
              {s.label} {s.value}
            </span>
          ))}
        </span>
      </div>
    </div>
  )
}

export default function GesturePoolStats({ characterId, refreshToken, onTargetPoolSize }: Props) {
  const [stats, setStats] = useState<Stats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const api = createAdminApi()
    setLoading(true)
    setError('')
    api
      .getCharacterGesturePoolStats(characterId)
      .then(s => {
        setStats(s)
        onTargetPoolSize?.(s.targetPoolSize)
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false))
  }, [characterId, refreshToken, onTargetPoolSize])

  if (loading) return <span className={styles.defaultNote}>Loading…</span>
  if (error) return <p className={styles.saveError}>{error}</p>
  if (!stats) return null

  const target = stats.targetPoolSize
  const empty = stats.totalEntries === 0

  // Tier 1 — headline tiles. Rendered even for an empty pool (all zeros).
  const tiles = (
    <>
      <div className={styles.statRow}>
        <div className={styles.statTile}>
          <span className={styles.statNum}>{stats.totalEntries}</span>
          <span className={styles.statLabel}>Entries</span>
        </div>
        <div className={styles.statTile}>
          <span className={styles.statNum}>{stats.distinctKeys}</span>
          <span className={styles.statLabel}>Distinct keys</span>
        </div>
        <div className={styles.statTile}>
          <span className={styles.statNum}>
            {stats.keysAtTarget} / {stats.distinctKeys}
          </span>
          <span className={styles.statLabel}>Keys at target</span>
          <span className={styles.statSub}>target {target} variants/key</span>
          <span className={styles.statSub}>median {round1(stats.medianEntriesPerKey)}/key</span>
        </div>
        <div className={styles.statTile}>
          <span className={styles.statNum}>{stats.totalPlays}</span>
          <span className={styles.statLabel}>Total plays</span>
          {stats.lastPlayedAt && (
            <span className={styles.statSub}>last {shortDate(stats.lastPlayedAt)}</span>
          )}
        </div>
      </div>
      {!empty && (
        <p className={styles.statSpan}>
          pool span {shortDate(stats.oldestCreatedAt)} – {shortDate(stats.newestCreatedAt)}
        </p>
      )}
    </>
  )

  if (empty) {
    return (
      <>
        {tiles}
        <span className={styles.defaultNote}>(pool is empty — no generated variants yet)</span>
      </>
    )
  }

  // Source is provenance → a neutral ramp of the theme's muted
  // grey. color-mix keeps it tracking --text-muted (which differs per theme:
  // #888 dark, #555 light) since there's no --text-muted-rgb to take opacity.
  const srcMix = [100, 70, 45, 28]
  const srcColor = (pct: number) => `color-mix(in srgb, var(--text-muted) ${pct}%, transparent)`
  // live and topup are the only sources the runtime writes. prewarm and import
  // are reserved in the schema's CHECK for flows that don't exist yet (see
  // migration 00031), so listing them as permanent "0" legend entries just
  // advertises features that aren't there. They're filtered rather than deleted
  // so the bar still accounts for every entry if those flows do start writing.
  const sourceSegs: Seg[] = [
    { label: 'live', value: stats.source.live, color: srcColor(srcMix[0]) },
    { label: 'topup', value: stats.source.topup, color: srcColor(srcMix[1]) },
    { label: 'prewarm', value: stats.source.prewarm, color: srcColor(srcMix[2]) },
    { label: 'import', value: stats.source.import, color: srcColor(srcMix[3]) },
  ].filter(s => s.value > 0 || s.label === 'live' || s.label === 'topup')

  return (
    <>
      {tiles}

      <StackedBar label="Source" segments={sourceSegs} />

      <div className={styles.statBarRow}>
        <span className={styles.statBarLabel}>Motion class</span>
        <span className={styles.statSplit}>
          gesture {stats.motionClass.gesture} · performance {stats.motionClass.performance}
        </span>
      </div>
    </>
  )
}
